import clsx from 'clsx';
import { useCallback, useRef } from 'react';

import { Field, fieldStyles, getFieldIconColor, useFieldId } from '../Field';
import { SelectField } from '../SelectField';
import { Typography } from '../typography';

import type { FieldSize, FieldVariant } from '../Field';
import type { SelectOption } from '../SelectField';
import type {
  ChangeEvent,
  DetailedHTMLProps,
  InputHTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  Ref,
} from 'react';

import s from './TimeField.module.css';

export type TimeFieldVariant = FieldVariant;
type TimeFieldSize = FieldSize;

export type TimeOption = SelectOption;

type BaseTimeFieldProps = {
  /** Container className */
  containerClassName?: string;
  /** Make field take full width of its container */
  fullWidth?: boolean;
  /** Helper text displayed below the field */
  hint?: ReactNode;
  /** Input/Select className */
  inputClassName?: string;
  /** Label text displayed after the time (shows when value is selected) */
  label?: string;
  /** Handler for time change */
  onTimeChange?: (value: string) => void;
  /** Custom time options - when provided, renders SelectField instead of time input */
  options?: TimeOption[];
  /** Placeholder text */
  placeholder?: string;
  /** Ref to the input or select element */
  ref?: Ref<HTMLInputElement | HTMLSelectElement>;
  /** Size of the field
   * @default 'default'
   */
  size?: TimeFieldSize;
  /** Element displayed at the end of the field */
  suffix?: ReactNode;
  /** Time value (HH:mm format) */
  value?: string;
  /** Visual state of the field
   * @default 'default'
   */
  variant?: TimeFieldVariant;
};

export type TimeFieldProps = BaseTimeFieldProps &
  Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'list' | 'ref' | 'size' | 'type'>;

export const TimeField = ({
  className,
  containerClassName,
  disabled,
  fullWidth,
  hint,
  id,
  inputClassName,
  label,
  onChange,
  onTimeChange,
  options,
  placeholder,
  ref,
  size = 'default',
  suffix,
  value,
  variant = 'default',
  ...props
}: TimeFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldId = useFieldId(id);

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  if (options) {
    return (
      <SelectField
        ref={ref as Ref<HTMLSelectElement>}
        containerClassName={containerClassName}
        disabled={disabled}
        fullWidth={fullWidth}
        hint={hint}
        id={id}
        label={label}
        onChange={(e) => onTimeChange?.(e.target.value)}
        options={options}
        placeholder={placeholder}
        selectClassName={clsx(inputClassName, className)}
        size={size}
        suffix={suffix}
        value={value}
        variant={variant}
      />
    );
  }

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hintId = hint ? `${fieldId}-hint` : undefined;

  const iconColor = getFieldIconColor(variant, disabled);

  const hasValue = value !== undefined && value !== '';
  const hasLabel = Boolean(label);
  const hasSuffix = Boolean(suffix) || (hasLabel && hasValue);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onTimeChange?.(e.target.value);
    onChange?.(e);
  };

  const handleWrapperClick = (e: MouseEvent) => {
    if (disabled) return;

    // Don't trigger if clicking on the input itself (it handles its own click)
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT') {
      return;
    }

    inputRef.current?.focus();
    inputRef.current?.showPicker?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Tab') {
      e.preventDefault();
      inputRef.current?.showPicker?.();
    }
  };

  return (
    <Field className={containerClassName} disabled={disabled} hint={hint} hintId={hintId} variant={variant}>
      <Field.Wrapper
        className={s.wrapper}
        disabled={disabled}
        fullWidth={fullWidth}
        hasSuffix={hasSuffix}
        onClick={handleWrapperClick}
        size={size}
        variant={variant}
      >
        <label className={fieldStyles.labelWrapper} htmlFor={fieldId}>
          <input
            ref={setInputRef}
            className={clsx(
              fieldStyles.control,
              fieldStyles[`control--variant-${variant}`],
              s.input,
              inputClassName,
              className,
            )}
            aria-describedby={hintId}
            aria-invalid={variant === 'danger'}
            disabled={disabled}
            id={fieldId}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            type="time"
            value={value}
            {...props}
          />

          {hasLabel && hasValue && (
            <Typography className={s.label} color={iconColor} component="span" variant="inset-paragraph">
              {label}
            </Typography>
          )}

          {Boolean(suffix) && <Field.Suffix color={iconColor}>{suffix}</Field.Suffix>}
        </label>
      </Field.Wrapper>
    </Field>
  );
};
